import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, orderBy, where, getDocs, collectionGroup, doc, getDoc, limit, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { getUserCountry, getPlan } from '../utils/countryPricing';
import ConversationList from './messaging/ConversationList';
import MessagePanel from './messaging/MessagePanel';
import { Bars3Icon, ArrowLeftIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { GiDominoMask, GiRadarSweep, GiSoundWaves, GiEchoRipples, GiSpeedometer } from "react-icons/gi";
import { MdContacts } from "react-icons/md";
import { ShieldCheckIcon, Cog6ToothIcon, XMarkIcon, ChevronRightIcon, ChevronLeftIcon, GlobeAltIcon, PlusIcon, PhoneIcon, UserPlusIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { listenConversations } from '../utils/firebaseListeners';
import { loadStripe } from '@stripe/stripe-js';
import InternationalCountryModal from './InternationalCountryModal';
import CountryFlag from './CountryFlag';
import { 
  formatInternationalPhoneNumber, 
  detectCountryFromPhoneNumber,
  getCountryByCode 
} from '../utils/countryUtils';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Function to get navigation items - keeping the Messages item visible in the MessageTrace page
function getFilteredNavigation(pathname) {
  return [
    { name: 'Home', href: '/overview', icon: GiSpeedometer, current: pathname === '/overview' },
    { name: 'Relay', href: '/dashboard', icon: GiDominoMask, current: pathname === '/dashboard' },
    { name: 'Messages', href: '/dashboard/trace', icon: GiEchoRipples, current: pathname === '/dashboard/trace' },
    { name: 'World Call', href: '/world-call', icon: GiSoundWaves, current: pathname === '/world-call' },
    { name: 'Contacts', href: '/contacts', icon: MdContacts, current: pathname === '/contacts' },
    { name: 'Monitor', href: '/dashboard/monitor', icon: GiRadarSweep, current: pathname === '/dashboard/monitor' },
  ];
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function MessageTrace() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [user, setUser] = useState(null);
  const [newMessageRecipient, setNewMessageRecipient] = useState('');
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [phoneAliases, setPhoneAliases] = useState([]);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isMessagingEnabled, setIsMessagingEnabled] = useState(false);
  const [showMessagingDialog, setShowMessagingDialog] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [isActivatingMessaging, setIsActivatingMessaging] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [messagingPrice, setMessagingPrice] = useState('+$3/mo'); // Default fallback
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedFromNumber, setSelectedFromNumber] = useState('');
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = getFilteredNavigation(location.pathname);
  // Define settingsNavItem here inside the component where location is available
  const settingsNavItem = { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, current: location.pathname === '/settings' };

  // Navigate to contacts page with phone number search
  const navigateToContact = (phoneNumber) => {
    // Store the phone number in localStorage for ContactsList to pick up
    localStorage.setItem('contactSearchNumber', phoneNumber);
    navigate('/contacts');
  };

  // Navigate to voice dialer with pre-filled number
  const navigateToVoiceDialer = (phoneNumber) => {
    // Store the phone number in localStorage for VoiceDialer to pick up
    localStorage.setItem('pendingCallNumber', phoneNumber);
    navigate('/world-call');
  };

  // Check for mobile view on component mount and window resize
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 1024); // lg breakpoint in Tailwind is 1024px
    };
    
    checkMobileView(); // Check on initial render
    window.addEventListener('resize', checkMobileView);
    
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  // Auto-select from number if user only has one active number
  useEffect(() => {
    if (phoneAliases.length > 0) {
      const activeNumbers = phoneAliases.filter(phone => 
        phone.type === 'voip' && 
        phone.connectionStatus && 
        phone.connectionStatus.toLowerCase() === 'active'
      );
      
      // If user has exactly one active number, auto-select it
      if (activeNumbers.length === 1) {
        setSelectedFromNumber(activeNumbers[0].aliasNumber);
      } else if (activeNumbers.length === 0) {
        // If no active numbers, clear selection
        setSelectedFromNumber('');
      }
      // If multiple numbers, don't auto-select - let user choose
    }
  }, [phoneAliases]);

  // Load user's phone numbers and conversations
  useEffect(() => {
    let previousUid = null;
    
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        previousUid = currentUser.uid;
        await loadPhoneAliases(currentUser.uid);
        // Load conversations with the real-time listener instead
        loadConversationsRealtime(currentUser.uid);
        
        // Fetch subscription data and check if messaging is enabled
        await fetchSubscriptionData(currentUser.uid);
        
        // Load dynamic messaging pricing
        try {
          const userCountry = await getUserCountry(currentUser.uid);
          const messagingPlan = getPlan(userCountry, 'messaging', 'quarterly', 1);
          if (messagingPlan) {
            // Calculate quarterly to monthly equivalent for display
            const quarterlyPrice = parseFloat(messagingPlan.price.replace(/^CA\$|\$/, ''));
            const monthlyEquivalent = (quarterlyPrice / 3).toFixed(2);
            const currency = messagingPlan.price.startsWith('CA$') ? 'CA$' : '$';
            setMessagingPrice(`+${currency}${monthlyEquivalent}/mo`);
          }
        } catch (error) {
          console.error('Error loading messaging price:', error);
          // Keep default fallback
        }
      } else {
        // Clear cache for previous user
        if (previousUid) {
          sessionStorage.removeItem(`phone_aliases_${previousUid}`);
          sessionStorage.removeItem(`subscription_data_${previousUid}`);
        }
        setUser(null);
        setConversations([]);
        setSelectedConversation(null);
        setPhoneAliases([]);
        setIsMessagingEnabled(false);
        setSubscription(null);
      }
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);

  // After conversations are loaded, check if we should show the new conversation UI
  useEffect(() => {
    // If loading is finished, we have no selected conversation, 
    // and either no conversations or we're not in mobile view, show new conversation UI
    if (!loading && !selectedConversation && 
        (conversations.length === 0 || !isMobileView) && !showNewConversation) {
      setShowNewConversation(true);
    }
  }, [loading, conversations, selectedConversation, isMobileView, showNewConversation]);

  // This effect ensures that when showNewConversation is manually set to true,
  // it takes precedence over selectedConversation
  useEffect(() => {
    if (showNewConversation) {
      // When showing the new conversation form, always clear the selected conversation
      setSelectedConversation(null);
    }
  }, [showNewConversation]);

  // This effect will automatically select the first conversation when they are loaded,
  // but only if we're not showing the new conversation form and not in mobile view
  useEffect(() => {
    if (
      !loading &&
      conversations.length > 0 &&
      !selectedConversation &&
      !showNewConversation &&
      !isMobileView
    ) {
      setSelectedConversation(conversations[0]);
    }
  }, [loading, conversations, selectedConversation, showNewConversation, isMobileView]);

  // This effect will check for incoming messages periodically to refresh conversations
  useEffect(() => {
    if (user?.uid) {
      // Immediately try to load conversations
      const refreshConversations = async () => {
        console.log("Refreshing conversations");
        try {
          // Query most recent messages collection (e.g., for current month)
          const now = new Date();
          const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          
          // Look for messages in all user phone aliases for current month
          const updatedConversations = [...conversations];
          let foundNew = false;
          
          for (const phoneAlias of phoneAliases) {
            const aliasNumber = phoneAlias.aliasNumber;
            const monthPath = `userMessaging/${user.uid}/phoneAliases/${aliasNumber}/${currentMonth}`;
            
            try {
              // Query messages for this phone alias in current month
              const messagesRef = collection(db, monthPath);
              const messagesQuery = query(messagesRef, orderBy('timestamp', 'desc'), limit(20));
              const messagesSnapshot = await getDocs(messagesQuery);
              
              if (!messagesSnapshot.empty) {
                console.log(`Found ${messagesSnapshot.size} recent messages for ${aliasNumber}`);
                
                // Process the messages to find unique conversations
                messagesSnapshot.forEach(doc => {
                  const messageData = doc.data();
                  const contactNumber = messageData.direction === 'inbound' 
                    ? messageData.from?.number 
                    : messageData.to?.number;
                  
                  if (contactNumber) {
                    // Check if this conversation already exists
                    const existingIndex = updatedConversations.findIndex(c => 
                      c.aliasNumber === aliasNumber && c.lastContactNumber === contactNumber
                    );
                    
                    if (existingIndex >= 0) {
                      // Update existing conversation
                      if (!updatedConversations[existingIndex].messageCollections) {
                        updatedConversations[existingIndex].messageCollections = [];
                      }
                      
                      if (!updatedConversations[existingIndex].messageCollections.includes(currentMonth)) {
                        updatedConversations[existingIndex].messageCollections.push(currentMonth);
                      }
                      
                      // Update last message timestamp if newer
                      if (messageData.timestamp && !updatedConversations[existingIndex].lastMessage) {
                        updatedConversations[existingIndex].lastMessage = new Date(
                          messageData.timestamp.seconds * 1000
                        ).toISOString();
                        foundNew = true;
                      }
                    } else {
                      // Create new conversation entry
                      updatedConversations.push({
                        aliasNumber,
                        lastContactNumber: contactNumber,
                        lastMessage: messageData.timestamp 
                          ? new Date(messageData.timestamp.seconds * 1000).toISOString() 
                          : null,
                        messageCollections: [currentMonth],
                        unread: 0,
                        connectionStatus: phoneAlias.connectionStatus && 
                          phoneAlias.connectionStatus.toLowerCase() === 'active' ? 
                          'active' : phoneAlias.connectionStatus,
                        type: phoneAlias.type
                      });
                      foundNew = true;
                    }
                  }
                });
              }
            } catch (error) {
              console.log(`No messages found for ${aliasNumber} in ${currentMonth}:`, error.message);
            }
          }
          
          if (foundNew) {
            console.log("Found new conversation data, updating state");
            setConversations(updatedConversations);
          }
        } catch (error) {
          console.error("Error refreshing conversations:", error.message);
        }
      };
      
      refreshConversations();
      
      // Set up interval to check for new messages
      const intervalId = setInterval(refreshConversations, 30000); // Every 30 seconds
      
      return () => clearInterval(intervalId);
    }
  }, [user, phoneAliases, conversations]);

  // New function to load conversations using real-time listener
  const loadConversationsRealtime = (uid) => {
    console.log("Setting up real-time conversation listener");
    setLoading(true);
    
    // Set up the listener for the new messagingplatform collection
    const unsubscribe = listenConversations(uid, (newConversations) => {
      console.log(`Received ${newConversations.length} conversations from real-time listener`);
      
      // Check if we found any platform conversations
      if (newConversations.length > 0) {
        setConversations(newConversations);
        setLoading(false);
      } else {
        // If no conversations in the new structure yet, fall back to the old method
        // This helps during transition period
        console.log("No conversations found in new structure, falling back to legacy loading");
        loadConversations(uid);
      }
    });
    
    // Store the unsubscribe function (though it's not used elsewhere in this component)
    // In a more complete implementation, you might want to store this in state or ref
    // and call it when appropriate
    return unsubscribe;
  };

  const loadPhoneAliases = async (uid) => {
    try {
      // Check for cached phone aliases first
      const cacheKey = `phone_aliases_${uid}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const { aliases, timestamp } = JSON.parse(cachedData);
          // Cache is valid for 2 minutes
          if (Date.now() - timestamp < 2 * 60 * 1000) {
            setPhoneAliases(aliases);
            return;
          }
        } catch (error) {
          console.error('Error parsing cached phone aliases:', error);
        }
      }

      // Load from Firestore if no valid cache
      const phoneAliasesRef = collection(db, 'userAliases', uid, 'phoneAliases');
      const phoneAliasesSnapshot = await getDocs(phoneAliasesRef);
      const phoneAliasesData = phoneAliasesSnapshot.docs.map(doc => ({
        id: doc.id,
          ...doc.data()
      }));
      
      setPhoneAliases(phoneAliasesData);
      
      // Cache the result
      sessionStorage.setItem(cacheKey, JSON.stringify({
        aliases: phoneAliasesData,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error loading phone aliases:', error);
    }
  };

  const loadConversations = async (uid) => {
    try {
      setLoading(true);
      
      // Get all phone aliases for this user
      const phoneAliasesSnapshot = await getDocs(collection(db, 'userAliases', uid, 'phoneAliases'));
      const phoneAliases = [];
      
      if (phoneAliasesSnapshot.empty) {
        console.log('No phone aliases found for user');
        setConversations([]);
        setPhoneAliases([]);
        setLoading(false);
        return;
      }
      
      // For each phone alias, get the conversation data
      const conversationsData = [];
      
      // Try to directly query the messages collection to find existing conversations
      try {
        // First approach: look for existing messages in the userMessaging collection
        const userMessagingRef = doc(db, 'userMessaging', uid);
        
        // Check if this path exists
        const userMessagingDoc = await getDoc(userMessagingRef);
        if (userMessagingDoc.exists()) {
          console.log("Found userMessaging document for user");
          
          // Try to list all phoneAliases subcollections
          const phoneAliasesMessagingRef = collection(userMessagingRef, 'phoneAliases');
          const phoneAliasesMessagingSnapshot = await getDocs(phoneAliasesMessagingRef);
          
          if (!phoneAliasesMessagingSnapshot.empty) {
            console.log(`Found ${phoneAliasesMessagingSnapshot.size} phone aliases with messages`);
            
            // Process each phone alias that has messages
            for (const phoneMessagingDoc of phoneAliasesMessagingSnapshot.docs) {
              const aliasNumber = phoneMessagingDoc.id;
              const phoneData = phoneMessagingDoc.data() || {};
              
              // Get the matching phone alias details
              const aliasDetails = phoneAliasesSnapshot.docs.find(doc => doc.id === aliasNumber);
              if (!aliasDetails) {
                console.log(`No alias details found for ${aliasNumber}, skipping`);
                continue;
              }
              
              const aliasData = aliasDetails.data();
              
              console.log(`Processing phone message history for: ${aliasNumber}`);
              
              // Get month collections for this phone alias
              const messageCollections = [];
              
              try {
                // List all subcollections
                const monthCollectionsPath = `userMessaging/${uid}/phoneAliases/${aliasNumber}`;
                console.log(`Checking for month collections at: ${monthCollectionsPath}`);
                
                // Use Firebase Admin SDK listCollections or similar if available
                // Since client SDK doesn't have direct support for listing collections,
                // we need to use an alternative approach:
                
                // Option 1: List documents in the collection as a workaround
                const phoneMessagingPath = `userMessaging/${uid}/phoneAliases/${aliasNumber}`;
                const phoneMessagingRef = doc(db, phoneMessagingPath);
                
                // Try to list all documents at this location
                try {
                  // This approach assumes some knowledge of the collection structure
                  // We'll look for known month formats (YYYY-MM)
                  const currentDate = new Date();
                  const currentYear = currentDate.getFullYear();
                  const currentMonth = currentDate.getMonth() + 1;
                  
                  // Check current month and previous months (up to 12 months back)
                  for (let i = 0; i < 12; i++) {
                    const checkDate = new Date(currentYear, currentMonth - i - 1);
                    const monthCollection = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}`;
                    
                    // Try to get at least one document from this collection to check if it exists
                    const monthCollectionRef = collection(db, phoneMessagingPath, monthCollection);
                    const monthQuery = query(monthCollectionRef, limit(1));
                    
                    try {
                      const monthSnapshot = await getDocs(monthQuery);
                      if (!monthSnapshot.empty) {
                        console.log(`Found messages in ${monthCollection}`);
                        messageCollections.push(monthCollection);
                      }
                    } catch (error) {
                      // Ignore errors for collections that don't exist
                      console.log(`No messages in ${monthCollection}`);
                    }
                  }
                } catch (error) {
                  console.error(`Error checking month collections for ${aliasNumber}:`, error.message);
                }
              } catch (error) {
                console.error(`Error listing collections for ${aliasNumber}:`, error.message);
              }
              
              // Add conversation if we have valid data
              if (phoneData.lastMessageFrom || phoneData.lastMessageTo) {
                const lastContactNumber = phoneData.lastMessageFrom || phoneData.lastMessageTo;
                console.log(`Found conversation with ${lastContactNumber} via ${aliasNumber}`);
                
                conversationsData.push({
                  aliasNumber,
                  lastContactNumber,
                  lastMessage: phoneData.lastMessage || null,
                  messageCollections,
                  unread: 0, // You can add logic to calculate unread messages
                  connectionStatus: aliasData.connectionStatus && 
                                   aliasData.connectionStatus.toLowerCase() === 'active' ? 
                                   'active' : aliasData.connectionStatus,
                  type: aliasData.type
                });
              }
            }
          } else {
            console.log("No phone aliases with messages found");
          }
        } else {
          console.log("No userMessaging document found for user");
        }
      } catch (error) {
        console.error("Error directly querying message history:", error.message);
      }
      
      // Try an alternative approach: Query all conversations from the conversationData collection
      if (conversationsData.length === 0) {
        try {
          console.log("Trying to find conversations in conversationData collection");
          const conversationsRef = collection(db, 'conversationData');
          const conversationsQuery = query(conversationsRef, where('uid', '==', uid));
          const conversationsSnapshot = await getDocs(conversationsQuery);
          
          if (!conversationsSnapshot.empty) {
            console.log(`Found ${conversationsSnapshot.size} conversations in conversationData`);
            
            conversationsSnapshot.forEach(doc => {
              const conversationData = doc.data();
              // Get the matching phone alias details
              const aliasDetails = phoneAliasesSnapshot.docs.find(phoneDoc => 
                phoneDoc.id === conversationData.aliasNumber
              );
              
              if (aliasDetails) {
                const aliasData = aliasDetails.data();
                conversationsData.push({
                  ...conversationData,
                  aliasNumber: conversationData.aliasNumber,
                  lastContactNumber: conversationData.lastContactNumber,
                  connectionStatus: aliasData.connectionStatus && 
                                   aliasData.connectionStatus.toLowerCase() === 'active' ? 
                                   'active' : aliasData.connectionStatus,
                  type: aliasData.type
                });
              }
            });
          }
        } catch (error) {
          console.error("Error querying conversationData:", error.message);
        }
      }
      
      // Add all phone aliases to the phoneAliases array for the dropdown
      for (const phoneDoc of phoneAliasesSnapshot.docs) {
        const aliasData = phoneDoc.data();
        const aliasNumber = phoneDoc.id;
        
        // Add to phoneAliases array
        phoneAliases.push({
          aliasNumber,
          ...aliasData
        });
      }
      
      console.log(`Loaded ${conversationsData.length} conversations`);
      setConversations(conversationsData);
      setPhoneAliases(phoneAliases);
      
      // We'll let the useEffect handle selection of conversations
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConversationSelect = (conversation) => {
    console.log("Selected conversation:", conversation);
    setSelectedConversation(conversation);
    setShowNewConversation(false);
  };

  const handleBackToConversations = () => {
    // Clear both states to ensure we go back to the conversation list
    setSelectedConversation(null);
    setShowNewConversation(false);
  };

  const handleNewConversation = () => {
    // First clear the selected conversation to prevent any flashing/persistence issues
    setSelectedConversation(null);
    
    // Then force a small delay before showing the new conversation UI
    // This ensures React has time to process the state update
    setTimeout(() => {
      setShowNewConversation(true);
      setNewMessageRecipient('');
    }, 50);
  };

  const handleStartNewConversation = async (recipientNumber, fromNumber) => {
    if (!recipientNumber || !fromNumber) {
      alert("Please enter both a recipient number and select a sender number");
      return;
    }
    
    // Check if messaging is enabled
    if (!isMessagingEnabled) {
      setShowMessagingDialog(true);
      return;
    }
    
    try {
      // Normalize the recipient phone number to proper US format
      const normalizedTo = normalizePhoneNumber(recipientNumber);
      
      // Normalize the from number for comparison
      const normalizedFrom = normalizePhoneNumber(fromNumber);
      
      // Prevent users from texting their own number (creates expensive loops)
      if (normalizedTo === normalizedFrom) {
        alert("You cannot send a message to the same number you're sending from. This would create a message loop.");
        return;
      }
      
      // Find the phone alias details
      const phoneAlias = phoneAliases.find(phone => phone.aliasNumber === fromNumber);
      
      if (!phoneAlias) {
        alert("Selected sender phone number is not valid");
        return;
      }
      
      // Check if the selected number has an active virtual number (voip type) relay
      const virtualNumberAliases = phoneAliases.filter(phone => 
        phone.type === 'voip' && 
        phone.connectionStatus && 
        phone.connectionStatus.toLowerCase() === 'active'
      );
      
      if (virtualNumberAliases.length === 0) {
        alert("You need to activate a virtual number relay first. Please go to your Relay dashboard and activate a number for messaging.");
        return;
      }
      
      console.log("Starting new conversation with:", {
        to: normalizedTo,
        from: fromNumber
      });
      
      // Create the conversation in messagingplatform collection with the correct structure
      const conversationId = `${fromNumber}_${normalizedTo}`;
      const conversationRef = doc(db, 'messagingplatform', user.uid, 'conversations', conversationId);
      await setDoc(conversationRef, {
        platformNumber: fromNumber,
        otherParty: normalizedTo,
        lastMessage: '',
        lastTimestamp: serverTimestamp(),
        unreadCount: 0
      });

      // Build conversation object for UI state
      const newConversation = {
        id: conversationId,
        platformNumber: fromNumber,
        aliasNumber: fromNumber,
        lastContactNumber: normalizedTo,
        otherParty: normalizedTo,
        lastMessage: '',
        lastTimestamp: new Date(),
        unreadCount: 0,
        connectionStatus: phoneAlias.connectionStatus.toLowerCase() === 'active' ? 'active' : phoneAlias.connectionStatus,
        type: phoneAlias.type || 'unknown'
      };

      // Update state and select new conversation
      setConversations(prev => [newConversation, ...prev]);
      setSelectedConversation(newConversation);
      setShowNewConversation(false);
      setNewMessageRecipient('');
      
      // Reset the from number selection (it will auto-select again if only one number)
      if (phoneAliases.filter(phone => 
        phone.type === 'voip' && 
        phone.connectionStatus && 
        phone.connectionStatus.toLowerCase() === 'active'
      ).length > 1) {
        setSelectedFromNumber('');
      }
    } catch (error) {
      console.error("Error creating new conversation:", error ? error.message : "Unknown error");
      alert("Error creating new conversation. Please try again.");
    }
  };

  // Function to fetch user subscription data
  const fetchSubscriptionData = async (uid) => {
    try {
      console.log(`Fetching subscription data for uid: ${uid}`);
      
      // Check for cached subscription data first
      const cacheKey = `subscription_data_${uid}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const { subscriptionData, messagingEnabled, timestamp } = JSON.parse(cachedData);
          // Cache is valid for 3 minutes
          if (Date.now() - timestamp < 3 * 60 * 1000) {
            console.log('Using cached subscription data:', { messagingEnabled });
            setSubscription(subscriptionData);
            setIsMessagingEnabled(messagingEnabled);
            return;
          }
        } catch (error) {
          console.error('Error parsing cached subscription data:', error);
        }
      }

      console.log('Fetching fresh subscription data from API...');
      
      // Fetch from API if no valid cache
      const response = await fetch(`/api/getUserSubscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('API response:', data);
        
        if (data.success) {
          const subscriptionData = data.subscriptionData;
          const messagingEnabled = data.messagingEnabled;
          
          console.log('Setting subscription state:', { 
            subscriptionFound: data.subscriptionFound,
            messagingEnabled 
          });
          
          setSubscription(subscriptionData);
          setIsMessagingEnabled(messagingEnabled);
          
          // Cache the result
          sessionStorage.setItem(cacheKey, JSON.stringify({
            subscriptionData: subscriptionData,
            messagingEnabled,
            timestamp: Date.now()
          }));
        } else {
          console.error('API returned error:', data.error);
        }
      } else {
        console.error('API request failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    }
  };

  // Function to handle the messaging toggle
  const handleMessagingToggle = async () => {
    if (!isMessagingEnabled) {
      // If turning on, show the upgrade dialog
      setShowMessagingDialog(true);
    } else {
      // If messaging is already enabled via the plan, don't allow toggling off
      // The messaging is now part of the subscription plan itself
      console.log('Messaging is enabled via subscription plan and cannot be toggled off');
    }
  };
  
  // Function to activate messaging by upgrading to messaging plan
  const activateMessaging = async () => {
    setIsActivatingMessaging(true);
    
    try {
      // Get the current subscription data
      if (!user?.uid) {
        throw new Error('User not authenticated');
      }
      
      // Check if user was on trial before upgrade
      const wasOnTrial = subscription?.subscriptionStatus === 'trialing' || subscription?.trialEndsAt;
      
      console.log('Starting messaging upgrade for user:', user.uid);
      
      // Call API to upgrade to messaging plan
      const response = await fetch('/api/upgrade-to-messaging', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: user.uid
        }),
      });
      
      console.log('API Response Status:', response.status, response.ok);
      
      let responseData;
      try {
        responseData = await response.json();
        console.log('API Response Data:', responseData);
      } catch (jsonError) {
        console.error('Error parsing JSON response:', jsonError);
        throw new Error('Invalid response from server');
      }
      
      // Handle SCA/3D Secure authentication requirements
      if (response.ok && responseData.requiresAction) {
        console.log('Payment requires additional authentication (SCA/3D Secure)');
        
        try {
          const stripe = await stripePromise;
          if (!stripe) {
            throw new Error('Stripe failed to initialize');
          }
          
          // Handle 3D Secure authentication
          const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
            responseData.clientSecret
          );
          
          if (confirmError) {
            console.error('3D Secure authentication failed:', confirmError);
            throw new Error(`Authentication failed: ${confirmError.message}`);
          }
          
          if (paymentIntent.status === 'succeeded') {
            console.log('3D Secure authentication successful, verifying subscription...');
            
            // Now verify the subscription status using our verification endpoint
            const verificationResponse = await fetch('/api/upgrade-to-messaging', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                uid: user.uid,
                verifySubscriptionId: responseData.subscriptionId
              }),
            });
            
            const verificationData = await verificationResponse.json();
            console.log('Subscription verification result:', verificationData);
            
            if (verificationResponse.ok && verificationData.success && verificationData.verified) {
              // Success! Subscription is now active
              setIsMessagingEnabled(true);
              
              // Clear subscription cache to get fresh data
              const cacheKey = `subscription_data_${user.uid}`;
              sessionStorage.removeItem(cacheKey);
              
              // Refresh subscription data
              await fetchSubscriptionData(user.uid);
              
              const message = wasOnTrial 
                ? 'Authentication successful! Your free trial has ended and your Relay Premium + Messaging subscription is now active!'
                : 'Authentication successful! Your Relay Premium + Messaging subscription has been activated!';
              
              setSuccessMessage(message);
              setShowSuccessMessage(true);
              
              setTimeout(() => {
                setShowSuccessMessage(false);
              }, 4000);
              
              setIsActivatingMessaging(false);
              setShowMessagingDialog(false);
              return;
              
            } else {
              // Verification failed
              const errorMsg = verificationData.message || 'Subscription verification failed after authentication';
              throw new Error(errorMsg);
            }
            
          } else {
            throw new Error(`Payment authentication incomplete: ${paymentIntent.status}`);
          }
          
        } catch (stripeError) {
          console.error('Stripe authentication error:', stripeError);
          throw stripeError;
        }
      }
      
      if (!response.ok) {
        console.log('API call failed with status:', response.status);
        console.log('Response data:', responseData);
        
        // Check if account was frozen due to upgrade failure
        if (responseData.accountFrozen) {
          console.log('Account was frozen, clearing caches and reloading...');
          
          // Account was frozen, clear ALL caches and reload to trigger frozen account check
          sessionStorage.removeItem(`subscription_data_${user.uid}`);
          sessionStorage.removeItem(`frozen_status_${user.uid}`);
          sessionStorage.removeItem(`phone_aliases_${user.uid}`);
          
          // Clear any other cached data that might interfere
          sessionStorage.clear();
          
          // Use window.location.replace to prevent going back to this state
          window.location.replace(window.location.href);
          return;
        }
        
        throw new Error(responseData.message || 'Failed to upgrade to messaging plan');
      }
      
      console.log('Successfully upgraded to messaging plan:', responseData);
      
      // Only show success message if we reach this point and are not reloading
      if (responseData.success) {
        // CRITICAL: Clear subscription cache before fetching fresh data
        const cacheKey = `subscription_data_${user.uid}`;
        sessionStorage.removeItem(cacheKey);
        console.log('Cleared subscription cache after successful upgrade');
        
        // Refresh subscription data to get the new subscription details
        await fetchSubscriptionData(user.uid);
        
        // Force messaging to be enabled immediately to prevent double billing
        setIsMessagingEnabled(true);
        console.log('Force-enabled messaging to prevent double billing');
        
        // Small delay to ensure state has updated
        setTimeout(() => {
          console.log('Post-upgrade messaging enabled state:', isMessagingEnabled);
          console.log('Post-upgrade subscription data:', subscription);
        }, 500);
        
        const message = wasOnTrial 
          ? 'Your free trial has ended successfully. Your Relay Premium + Messaging subscription is now active!'
          : 'Your Relay Premium + Messaging subscription has been activated successfully!';
        
        setSuccessMessage(message);
        setShowSuccessMessage(true);
        
        // Auto-hide success message after 4 seconds
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 4000);
        
        // Reset states only on success
        setIsActivatingMessaging(false);
        setShowMessagingDialog(false);
      }
    } catch (error) {
      console.error('Error upgrading to messaging plan:', error);
      alert(`There was an error upgrading to the messaging plan: ${error.message}`);
      
      // Reset states on error (but not if we're reloading)
      setIsActivatingMessaging(false);
      setShowMessagingDialog(false);
    }
  };


  // Handle country selection
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    
    // If there's already a number entered, reformat it with the new country code
    if (newMessageRecipient && newMessageRecipient.trim()) {
      const formattedNumber = formatInternationalPhoneNumber(newMessageRecipient, country.code);
      setNewMessageRecipient(formattedNumber);
    }
  };

  // Auto-detect country from phone number
  useEffect(() => {
    if (newMessageRecipient && newMessageRecipient.startsWith('+')) {
      const detectedCountry = detectCountryFromPhoneNumber(newMessageRecipient);
      if (detectedCountry && (!selectedCountry || selectedCountry.code !== detectedCountry.code)) {
        setSelectedCountry(detectedCountry);
      }
    }
  }, [newMessageRecipient, selectedCountry]);

  // Function to format phone numbers for international use
  const normalizePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Use the new international formatter
    return formatInternationalPhoneNumber(phoneNumber, selectedCountry?.code);
  };

  return (
    <>
      <div className="flex flex-col min-h-screen text-white" 
           style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}>
        {/* Mobile sidebar - Liquid Glass */}
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                  </button>
                </div>
              </TransitionChild>

              {/* Sidebar component - Liquid Glass */}
              <div 
                className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl"
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                }}
              >
                <div className="flex h-16 shrink-0 items-center justify-center px-4">
                  <img alt="Relay" src="/images/logowhite.png" className="h-8 w-auto" />
                    </div>
                <nav className="flex flex-1 flex-col px-4">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                      <ul role="list" className="space-y-3">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <Link
                                to={item.href}
                              className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                              onClick={() => setSidebarOpen(false)}
                            >
                              <item.icon 
                                aria-hidden="true" 
                                className={classNames(
                                  "h-6 w-6 shrink-0 transition-colors duration-200",
                                  item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                                )}
                              />
                              <span className={classNames(
                                "transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )}>
                                {item.name}
                              </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    <li className="mt-auto mb-4">
                    <Link
                      to={settingsNavItem.href}
                        className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <settingsNavItem.icon 
                          aria-hidden="true" 
                      className={classNames(
                            "h-6 w-6 shrink-0 transition-colors duration-200",
                            settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                          )} 
                        />
                        <span className={classNames(
                          "transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )}>
                      {settingsNavItem.name}
                        </span>
                    </Link>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Desktop sidebar - Liquid Glass Collapsible */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${
          sidebarExpanded ? 'lg:w-72' : 'lg:w-20'
        }`}>
          <div className="relative">
            <div 
              className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl relative"
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)',
                height: 'calc(100vh - 2rem)'
              }}
            >
              {/* Header with logo */}
              <div className={`flex h-16 shrink-0 items-center justify-center transition-all duration-300 ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                <img
                  alt="Relay"
                  src="/images/logowhite.png"
                  className={`transition-all duration-300 ${sidebarExpanded ? 'h-8 w-auto' : 'h-10 w-auto max-w-none'}`}
                  style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.1))' }}
                />
              </div>

              <nav className={`flex flex-1 flex-col ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="space-y-3">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={classNames(
                              'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                              sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                            )}
                            title={!sidebarExpanded ? item.name : undefined}
                    >
                            <item.icon 
                              aria-hidden="true" 
                              className={classNames(
                                "h-6 w-6 shrink-0 transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )} 
                            />
                            {sidebarExpanded && (
                              <span className={classNames(
                                "transition-opacity duration-300",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )}>
                                {item.name}
                              </span>
                            )}
                    </Link>
                  </li>
                ))}
              </ul>
                  </li>
                  <li className="mt-auto mb-4">
              <Link
                to={settingsNavItem.href}
                className={classNames(
                        'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                        sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                      )}
                      title={!sidebarExpanded ? settingsNavItem.name : undefined}
              >
                      <settingsNavItem.icon 
                        aria-hidden="true" 
                        className={classNames(
                          "h-6 w-6 shrink-0 transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )} 
                      />
                      {sidebarExpanded && (
                        <span className={classNames(
                          "transition-opacity duration-300",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )}>
                          {settingsNavItem.name}
                        </span>
                      )}
              </Link>
                  </li>
                </ul>
          </nav>
            </div>

            {/* Right border expand/collapse area */}
            <div 
              className="absolute top-0 right-0 w-4 h-full cursor-pointer group flex items-center justify-center"
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              style={{ zIndex: 60 }}
            >
              {/* Hover indicator */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20">
                {sidebarExpanded ? (
                  <ChevronLeftIcon className="h-4 w-4 text-white/70" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-white/70" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className={`flex-1 transition-all duration-300 ${
          sidebarExpanded ? 'lg:pl-80' : 'lg:pl-28'
        }`}>
          {/* Header - Liquid Glass */}
          <div 
            className="flex h-16 shrink-0 items-center gap-x-4 border-b border-white/10 px-4 shadow-lg m-4 mt-0 mb-0 rounded-t-2xl backdrop-blur-2xl"
            style={{
              background: 'rgba(0, 0, 0, 0.2)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
            }}
          >
            {isMobileView && selectedConversation && (
              <button 
                type="button" 
                className="mr-2 flex items-center text-white/60 hover:text-white transition-colors duration-200"
                onClick={handleBackToConversations}
              >
                <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" />
                <span className="ml-1">Back</span>
              </button>
            )}
            {(!isMobileView || !selectedConversation) && (
              <button type="button" className="-m-2 p-3 text-white lg:hidden bg-white/5 backdrop-blur-md rounded-md hover:bg-white/10 transition-all duration-200" onClick={() => setSidebarOpen(true)}>
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>
            )}
            <h1 className="text-xl font-semibold text-white">
              {isMobileView && selectedConversation ? 'Conversation' : 'Messages'}
            </h1>
            
            {/* Messaging Toggle with Info Icon */}
            <div className="ml-auto flex items-center">
              <div className="relative mr-2">
                <InformationCircleIcon 
                  className="h-5 w-5 text-white/60 hover:text-white/80 cursor-help transition-colors duration-200"
                  onMouseEnter={() => setTooltipVisible(true)}
                  onMouseLeave={() => setTooltipVisible(false)}
                />
                {tooltipVisible && (
                  <div 
                    className="absolute right-0 z-10 mt-2 w-72 origin-top-right rounded-xl p-4 text-sm text-white backdrop-blur-2xl border border-white/10 shadow-2xl"
                    style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                    }}
                  >
                    Instant Messaging is not included in your current subscription. You need to upgrade to the messaging plan ($7.99/mo billed quarterly) to send and receive messages entirely online.
                  </div>
                )}
              </div>
              <div className="flex items-center">
                <span className="mr-2 text-sm text-white/60">
                  {isMessagingEnabled ? 'Messaging Plan Active' : 'Not Included'}
                </span>
                <button
                  type="button"
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    isMessagingEnabled 
                      ? 'bg-indigo-600 cursor-default' 
                      : 'bg-white/20 cursor-pointer hover:bg-white/30'
                  }`}
                  onClick={handleMessagingToggle}
                  aria-pressed={isMessagingEnabled}
                  disabled={isMessagingEnabled}
                >
                  <span className="sr-only">Toggle messaging</span>
                  <span
                    className={`pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      isMessagingEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  ></span>
                </button>
              </div>
            </div>
          </div>

          {/* Content area with fixed height and no scrolling at outer level */}
          <div className="fixed inset-0 top-16 lg:left-20 overflow-hidden">
            {/* Two-panel layout that stays fixed with internal scrolling */}
            <div className="flex h-full p-4 pt-0 gap-4">
              {/* Conversation List - show only on desktop or mobile when no conversation selected */}
              {(!isMobileView || (!selectedConversation && !showNewConversation)) && (
                <div 
                  className="w-full lg:w-80 border border-white/10 rounded-2xl backdrop-blur-2xl flex flex-col overflow-hidden"
                  style={{
                    background: 'rgba(0, 0, 0, 0.2)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.2)'
                  }}
                >
                  <div className="h-full overflow-y-auto">
                    <ConversationList 
                      conversations={conversations} 
                      selectedConversation={selectedConversation}
                      onSelectConversation={handleConversationSelect}
                      onNewConversation={handleNewConversation}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
              
              {/* Message Panel - show on desktop always or mobile when conversation selected */}
              {(selectedConversation && !showNewConversation) && (
                <div 
                  className="flex-1 overflow-hidden flex flex-col border border-white/10 rounded-2xl backdrop-blur-2xl"
                  style={{
                    background: 'rgba(0, 0, 0, 0.2)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.2)'
                  }}
                >
                  <MessagePanel 
                    conversation={selectedConversation}
                    userId={user?.uid}
                    onNavigateToContact={navigateToContact}
                    onNavigateToVoiceDialer={navigateToVoiceDialer}
                  />
                </div>
              )}
              
              {/* New Conversation Form */}
              {showNewConversation && (
                <div 
                  className="flex-1 flex flex-col border border-white/10 rounded-2xl backdrop-blur-2xl overflow-hidden"
                  style={{
                    background: 'rgba(0, 0, 0, 0.2)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.2)'
                  }}
                >
                  <div className="p-4 border-b border-white/10 flex justify-between items-center">
                    <h2 className="text-lg font-medium text-white">New Conversation</h2>
                    {isMobileView && (
                      <button
                        type="button"
                        className="text-white/60 hover:text-white transition-colors duration-200"
                        onClick={handleBackToConversations}
                      >
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-center p-8 overflow-y-auto">
                    <div className="w-full max-w-md space-y-6">
                      <div>
                        <label htmlFor="recipient" className="block text-sm font-medium text-white/80">
                          Recipient Phone Number
                        </label>
                        
                        {/* Country Selection */}
                        <div className="mt-2 mb-3">
                          <button
                            type="button"
                            onClick={() => setShowCountryModal(true)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200"
                          >
                            {selectedCountry ? (
                              <>
                                <CountryFlag countryCode={selectedCountry.code} size="sm" />
                                <span className="text-sm">{selectedCountry.name}</span>
                                <span className="text-xs text-white/60">{selectedCountry.dialingCode}</span>
                              </>
                            ) : (
                              <>
                                <GlobeAltIcon className="h-4 w-4" />
                                <span className="text-sm">Select Country</span>
                              </>
                            )}
                          </button>
                        </div>

                        <div className="mt-1">
                          <div className="relative">
                            {selectedCountry && (
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-white/60 text-sm">{selectedCountry.dialingCode}</span>
                              </div>
                            )}
                            <input
                              type="text"
                              name="recipient"
                              id="recipient"
                              className={`block w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3 text-white placeholder-white/40 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10 sm:text-sm transition-all duration-200 ${
                                selectedCountry ? 'pl-20' : ''
                              }`}
                              placeholder={selectedCountry ? "123-456-7890" : "Enter phone number or select country first"}
                              value={newMessageRecipient}
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                setNewMessageRecipient(inputValue);
                              }}
                            />
                          </div>
                          {newMessageRecipient && newMessageRecipient !== normalizePhoneNumber(newMessageRecipient) && (
                            <p className="mt-1 text-xs text-indigo-400">
                              Will be formatted as: {normalizePhoneNumber(newMessageRecipient)}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="fromNumber" className="block text-sm font-medium text-white/80">
                          From Number
                        </label>
                        <div className="mt-1">
                          <select
                            id="fromNumber"
                            name="fromNumber"
                            value={selectedFromNumber}
                            onChange={(e) => setSelectedFromNumber(e.target.value)}
                            className="block w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10 sm:text-sm transition-all duration-200"
                          >
                            <option value="">
                              {phoneAliases.filter(phone => 
                                phone.type === 'voip' && 
                                phone.connectionStatus && 
                                phone.connectionStatus.toLowerCase() === 'active'
                              ).length === 1 ? 'Auto-selected' : 'Select a number'}
                            </option>
                            {phoneAliases.map((phone) => (
                              <option key={phone.aliasNumber} value={phone.aliasNumber}>
                                {phone.aliasNumber} {phone.type ? `(${phone.type})` : ''} 
                                {phone.connectionStatus && phone.connectionStatus.toLowerCase() === 'active' ? ' ✓' : ' (Inactive)'}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Show virtual number requirement notice */}
                        {phoneAliases.filter(phone => 
                          phone.type === 'voip' && 
                          phone.connectionStatus && 
                          phone.connectionStatus.toLowerCase() === 'active'
                        ).length === 0 && (
                          <div className="mt-2 p-3 rounded-xl bg-yellow-500/20 border border-yellow-500/30">
                            <p className="text-xs text-yellow-200">
                              <strong>Note:</strong> You need an active virtual number relay to send messages. 
                              <Link to="/dashboard" className="text-yellow-100 underline hover:text-white transition-colors">
                                Go to Relay dashboard
                              </Link> to activate one.
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-xl border border-indigo-500/30 bg-indigo-600/80 backdrop-blur-sm py-3 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-600 hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-200"
                        onClick={() => {
                          handleStartNewConversation(newMessageRecipient, selectedFromNumber);
                        }}
                      >
                        Start Conversation
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Success Message - Liquid Glass */}
      {showSuccessMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
          <div 
            className="text-white px-6 py-4 rounded-xl shadow-2xl max-w-md border border-white/10 backdrop-blur-2xl"
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-indigo-600/80 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <ShieldCheckIcon className="h-5 w-5 text-white" aria-hidden="true" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-white">{successMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Messaging Upgrade Dialog - Liquid Glass */}
      {showMessagingDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" 
             style={{background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)'}}>
          <div 
            className="rounded-2xl max-w-md w-full p-6 border border-white/10 backdrop-blur-2xl"
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <h3 className="text-xl font-bold text-white mb-4">Upgrade to Messaging Plan</h3>
            <p className="text-white/80 mb-6">
              Instant Messaging is not included in your current subscription. To send and receive messages entirely online activate Messaging for {messagingPrice} billed quarterly.
            </p>
            <div className="flex justify-between gap-3">
              <button
                onClick={() => setShowMessagingDialog(false)}
                className="rounded-xl bg-white/10 backdrop-blur-sm px-4 py-2 text-sm font-medium text-white hover:bg-white/20 transition-all duration-200 border border-white/10"
              >
                Cancel
              </button>
              <button
                onClick={activateMessaging}
                disabled={isActivatingMessaging}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {isActivatingMessaging ? 'Activating...' : 'Activate Messaging'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* International Country Selection Modal */}
      <InternationalCountryModal
        isOpen={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        onSelectCountry={handleCountrySelect}
      />
    </>
  );
} 